// The tour of the constellation (Jérémy, 23/09: "a cursor settles on a skill, and a tooltip opens with its full
// description"): a simulated pointer visits every skill in a shuffled order, the chip answers as it does under a real
// mouse (skills.tsx, fire), and a tooltip reads the skill out. Everything here is pure or takes its host as an
// argument, so a seeded generator and fake timers can check it; skills.tsx owns the DOM. No "use client": nothing here
// touches React.

/** A number in [0, 1), like Math.random. */
type Random = () => number;

/** What a tooltip says under the skill's name. */
export type Tip = { blurb: string; domain: string; jurisdiction: string };

/* The tooltips of the twelve chips, all held here so that no page of the skills library ships to the browser. A chip
   whose name matches a library page (case aside) carries that page's text, copied word for word: today that is NDA
   review alone, and tests/skills-cursor.test.tsx fails the day the two drift apart. The others have no page yet: one
   sentence each, in the library's register. DRAFT, to be approved by Jérémy (23/09). */
export const LOCAL_TIPS: Record<string, Tip> = {
  "NDA review": {
    blurb: "Fast triage of inbound NDAs into green, yellow, red. Lawyer time goes only to the ones that need it.",
    domain: "Commercial",
    jurisdiction: "Generic",
  },
  "Closing binder": {
    blurb: "Assembles the executed closing documents into one indexed binder, organized the way your firm hands a deal over.",
    domain: "Real estate",
    jurisdiction: "Generic",
  },
  "Signature packet": {
    blurb: "Pulls the signature pages out of every deal document and builds one packet per signatory, ready to send.",
    domain: "Corporate",
    jurisdiction: "Generic",
  },
  "Title review": {
    blurb: "Reads the title commitment and its exceptions, flags what blocks the deal, and lists what the seller must clear.",
    domain: "Real estate",
    jurisdiction: "United States",
  },
  "Entity formation": {
    blurb: "Drafts the formation documents for a new entity from the client's answers, on your firm's own forms.",
    domain: "Corporate",
    jurisdiction: "United States",
  },
  "Deadline calendar": {
    blurb: "Pulls every date and deadline out of a matter's documents onto one calendar, each one cited to its source.",
    domain: "Cross-practice",
    jurisdiction: "Generic",
  },
  "Client letter": {
    blurb: "Drafts a client update from the matter file in your firm's voice, for a lawyer to review before it goes out.",
    domain: "Cross-practice",
    jurisdiction: "Generic",
  },
  "Diligence index": {
    blurb: "Indexes a data room by topic and document type, and marks what is missing against the request list.",
    domain: "Corporate",
    jurisdiction: "Generic",
  },
  "Cap table": {
    blurb: "Rebuilds the cap table from the issuance documents and flags every place where the ledger and the paper disagree.",
    domain: "Corporate",
    jurisdiction: "United States",
  },
  Intake: {
    blurb: "Turns a new client inquiry into a structured intake record: parties, matter type, key dates and open questions.",
    domain: "Cross-practice",
    jurisdiction: "Generic",
  },
  "Conflict check": {
    blurb: "Checks the parties to a new matter against the firm's clients and matters, and lists every potential conflict.",
    domain: "Cross-practice",
    jurisdiction: "Generic",
  },
  "Matter brief": {
    blurb: "Summarizes where a matter stands from its documents and email: parties, status, next steps, each point cited.",
    domain: "Cross-practice",
    jurisdiction: "Generic",
  },
};

/** The tooltip of a chip. */
export function tipFor(label: string): Tip | undefined {
  return LOCAL_TIPS[label];
}

export const TOUR = {
  /** The cursor fades in beside the chip, in ms. */
  appear: 60,
  /** It travels onto the chip, on a slight arc, fast in the middle, and lands a few px past it, in ms (cursorPath,
      played by skills.tsx). Jérémy, 23/09: "plus légèrement, avec un petit bounce, car là c'est très robotique". */
  glide: 280,
  /** Then it comes back to rest on the chip, in ms: the bounce. The tooltip is already opening. */
  settle: 120,
  /** The tooltip opens, from the moment the cursor lands, with the ball, in ms (skills.css). Jérémy, 23/09: "il faut
      que l'affichage des tooltips apparaisse bien plus vite": readable less than 500 ms after the cursor appears. */
  open: 150,
  /** The tooltip stays open, breathing, in ms. */
  hold: 1700,
  /** The cursor fades out once the tooltip has closed (it closes in `open`), in ms. */
  close: 150,
  /** Between two visits, in ms. */
  pause: [400, 800],
  /** How far from its chip the cursor appears, in px. */
  offset: [60, 120],
  /** How far the middle of its path bows off the straight line, as a share of the distance (on a side drawn at
      random). */
  bend: [0.1, 0.2],
  /** How far past the chip it lands before coming back, in px. */
  overshoot: [3, 5],
  /** The map on screen (or the reveals over): the first visit comes this long after, in ms. */
  first: 900,
} as const;

const between = (random: Random, [min, max]: readonly [number, number]) => Math.round(min + random() * (max - min));

/** One round of the tour: every skill once, shuffled (Fisher-Yates), and never starting on `last`, the skill the
    previous round ended on, so no skill is ever visited twice in a row, even across two rounds. */
export function planTour(random: Random, count: number, last: number | null): number[] {
  const order = Array.from({ length: count }, (_, i) => i);
  for (let i = count - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  if (count > 1 && order[0] === last) {
    const k = 1 + Math.floor(random() * (count - 1));
    [order[0], order[k]] = [order[k]!, order[0]!];
  }
  return order;
}

export type Visit = { dx: number; dy: number; bend: number; overshoot: number; pause: number };

const betweenReal = (random: Random, [min, max]: readonly [number, number]) => min + random() * (max - min);

/** Where the cursor appears relative to its chip (a direction drawn at random, 60 to 120 px away), how its path bows
    (a signed share of the distance: the sign is the side) and overshoots, and the pause after the visit. */
export function planVisit(random: Random): Visit {
  const angle = random() * 2 * Math.PI;
  const distance = between(random, TOUR.offset);
  const side = random() < 0.5 ? -1 : 1;
  return {
    dx: Math.round(Math.cos(angle) * distance) || 0,
    dy: Math.round(Math.sin(angle) * distance) || 0,
    bend: side * Math.round(betweenReal(random, TOUR.bend) * 1000) / 1000,
    overshoot: Math.round(betweenReal(random, TOUR.overshoot) * 10) / 10,
    pause: between(random, TOUR.pause),
  };
}

/** One point of the cursor's path, in px from where it rests on the chip, at `offset` (0 to 1) of glide + settle. */
export type PathPoint = { x: number; y: number; offset: number };

/** The travel of a hand that knows where it is going: a gentle start, fast in the middle and a longer braking
    (minimum jerk, its time bent so the peak speed comes before halfway). 0 to 1 over 0 to 1. */
const travel = (t: number) => {
  const u = t ** 0.8;
  return u * u * u * (10 - 15 * u + 6 * u * u);
};

/** The cursor's path for one visit, sampled every `step` ms, played linearly (skills.tsx, element.animate): from
    (dx, dy) on one arc (a quadratic curve whose middle sits `bend` of the distance off the straight line) to
    `overshoot` px past the chip along its last heading at TOUR.glide, then back to rest on the chip, easing in and
    out, at TOUR.glide + TOUR.settle. */
export function cursorPath(visit: Visit, step = 20): PathPoint[] {
  const total = TOUR.glide + TOUR.settle;
  const distance = Math.hypot(visit.dx, visit.dy) || 1;
  // The curve's middle: bend x distance off the middle of the straight line, square to it.
  const mx = visit.dx / 2 - (visit.dy / distance) * visit.bend * distance;
  const my = visit.dy / 2 + (visit.dx / distance) * visit.bend * distance;
  // Past the chip along the heading of that curve drawn onto the chip (from its control point to the chip)...
  const heading = Math.hypot(2 * mx - visit.dx / 2, 2 * my - visit.dy / 2) || 1;
  const ox = (-(2 * mx - visit.dx / 2) / heading) * visit.overshoot;
  const oy = (-(2 * my - visit.dy / 2) / heading) * visit.overshoot;
  // ...and the control point of the curve that ends there, through the same middle.
  const cx = 2 * mx - (visit.dx + ox) / 2;
  const cy = 2 * my - (visit.dy + oy) / 2;
  const curve = (t: number) => ({
    x: (1 - t) ** 2 * visit.dx + 2 * (1 - t) * t * cx + t * t * ox,
    y: (1 - t) ** 2 * visit.dy + 2 * (1 - t) * t * cy + t * t * oy,
  });
  const round = (n: number) => Math.round(n * 100) / 100 || 0;
  const points: PathPoint[] = [];
  const glideSteps = Math.max(2, Math.round(TOUR.glide / step));
  for (let i = 0; i <= glideSteps; i++) {
    const at = curve(travel(i / glideSteps));
    points.push({ x: round(at.x), y: round(at.y), offset: Number((((i / glideSteps) * TOUR.glide) / total).toFixed(4)) });
  }
  const settleSteps = Math.max(2, Math.round(TOUR.settle / step));
  for (let i = 1; i <= settleSteps; i++) {
    const back = 1 - (1 - Math.cos((Math.PI * i) / settleSteps)) / 2;
    points.push({ x: round(ox * back), y: round(oy * back), offset: Number(((TOUR.glide + (i / settleSteps) * TOUR.settle) / total).toFixed(4)) });
  }
  points[points.length - 1]!.offset = 1;
  return points;
}

export type Box = { x: number; y: number; w: number; h: number };

/** Where the tooltip may sit around its chip, in order of preference (Jérémy, 23/09: "le tooltip placé à l'écart des
    chips voisins"). */
export const TIP_SIDES = ["above", "below", "left", "right", "above-left", "above-right", "below-left", "below-right"] as const;
export type TipSide = (typeof TIP_SIDES)[number];

/** Area, in px², that box `a` shares with box `b` grown by `margin` on every side. */
const overlap = (a: Box, b: Box, margin: number) =>
  Math.max(0, Math.min(a.x + a.w, b.x + b.w + margin) - Math.max(a.x, b.x - margin)) *
  Math.max(0, Math.min(a.y + a.h, b.y + b.h + margin) - Math.max(a.y, b.y - margin));

/** Where the tooltip goes, in px from the map's top left. Each side of TIP_SIDES in turn, `gap` away from the chip:
    first centred on it (slid back inside the map if need be), then slid along that side 8 px at a time, nearest first,
    as long as the chip's middle stays `reach` px inside the tooltip's span — a corner is not slid. The first spot that
    stays `margin` inside the map and keeps `margin` clear of every box in `avoid` (the other chips, the disc) wins.
    None does: the one inside the map that covers the least of them, by area. None fits the map at all: above or below,
    whichever has more room, clamped inside it. `clear` says whether the winner touches nothing. */
export function placeTip(chip: Box, tip: { w: number; h: number }, map: { w: number; h: number }, avoid: readonly Box[] = [], gap = 12, margin = 8) {
  const reach = 24;
  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
  const cx = chip.x + chip.w / 2;
  const cy = chip.y + chip.h / 2;
  const up = chip.y - gap - tip.h;
  const down = chip.y + chip.h + gap;
  const west = chip.x - gap - tip.w;
  const east = chip.x + chip.w + gap;
  const slideX = (x: number) => clamp(x, margin, map.w - tip.w - margin);
  const slideY = (y: number) => clamp(y, margin, map.h - tip.h - margin);
  // Centred first, then every 8 px along the side, nearest first.
  const along = (mid: number, min: number, max: number) => {
    const out = [mid];
    for (let d = 8; mid - d >= min || mid + d <= max; d += 8) {
      if (mid - d >= min) out.push(mid - d);
      if (mid + d <= max) out.push(mid + d);
    }
    return out;
  };
  const xs = along(cx - tip.w / 2, cx - tip.w + reach, cx - reach);
  const ys = along(cy - tip.h / 2, cy - tip.h + reach, cy - reach);
  const spots: Record<TipSide, { x: number; y: number }[]> = {
    above: xs.map((x, i) => ({ x: i ? x : slideX(x), y: up })),
    below: xs.map((x, i) => ({ x: i ? x : slideX(x), y: down })),
    left: ys.map((y, i) => ({ x: west, y: i ? y : slideY(y) })),
    right: ys.map((y, i) => ({ x: east, y: i ? y : slideY(y) })),
    "above-left": [{ x: west, y: up }],
    "above-right": [{ x: east, y: up }],
    "below-left": [{ x: west, y: down }],
    "below-right": [{ x: east, y: down }],
  };
  const fits = ({ x, y }: { x: number; y: number }) => x >= margin && y >= margin && x + tip.w <= map.w - margin && y + tip.h <= map.h - margin;
  let best: { side: TipSide; x: number; y: number; covered: number } | null = null;
  search: for (const side of TIP_SIDES) {
    for (const spot of spots[side]) {
      if (!fits(spot)) continue;
      const covered = avoid.reduce((sum, box) => sum + overlap({ ...spot, ...tip }, box, margin), 0);
      if (!best || covered < best.covered) best = { side, ...spot, covered };
      if (covered === 0) break search;
    }
  }
  if (best) return { x: Math.round(best.x), y: Math.round(best.y), side: best.side, clear: best.covered === 0 };
  const room = { above: chip.y - gap - margin, below: map.h - down - margin };
  const side: TipSide = room.above >= room.below ? "above" : "below";
  return { x: Math.round(slideX(cx - tip.w / 2)), y: Math.round(slideY(side === "above" ? up : down)), side, clear: false };
}

/** The stages of one visit, set as data-tour on the map (skills.css):
    off   nothing shown (the cursor fades out where it is);
    at    the cursor, faded in beside its chip;
    on    the cursor travelling onto the chip (skills.tsx plays cursorPath);
    tip   on the chip (its bounce still settling), the tooltip open and breathing;
    leave the tooltip closing, the cursor still there. */
export type TourStage = "off" | "at" | "on" | "tip" | "leave";

export type TourHost = {
  count: number;
  /** Ms before a visit may start (reveals still playing, map not in view yet); 0 to go. */
  wait(): number;
  /** Measures the chip, places cursor and tooltip, fills the tooltip. False if the chip cannot be shown. */
  show(index: number, visit: Visit): boolean;
  stage(stage: TourStage): void;
  /** The cursor has landed: the chip answers as it does to a real pointer. */
  arrive(index: number): void;
};

/** The tour's clock. Its timers are its own (a press on the star drops the volleys' timers, not these), and stop()
    drops every one of them: nothing it booked runs after. */
export function createTour(host: TourHost, random: Random = Math.random) {
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let order: number[] = [];
  let next = 0;
  let last: number | null = null;
  let running = false;
  let held = false;

  const later = (fn: () => void, ms: number) => {
    const id = setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
  };
  const clear = () => {
    for (const id of timers) clearTimeout(id);
    timers.clear();
  };

  const visit = () => {
    if (held) return;
    const wait = host.wait();
    if (wait > 0) return later(visit, wait);
    if (next >= order.length) {
      order = planTour(random, host.count, last);
      next = 0;
    }
    const index = order[next++]!;
    last = index;
    const plan = planVisit(random);
    if (!host.show(index, plan)) return later(visit, plan.pause);
    host.stage("at");
    let at = TOUR.appear;
    later(() => host.stage("on"), at);
    at += TOUR.glide;
    later(() => {
      host.arrive(index);
      host.stage("tip");
    }, at);
    at += TOUR.open + TOUR.hold;
    later(() => host.stage("leave"), at);
    at += TOUR.open;
    later(() => host.stage("off"), at);
    later(visit, at + TOUR.close + plan.pause);
  };

  return {
    start(delay: number) {
      if (running) return;
      running = true;
      later(visit, delay);
    },
    stop() {
      clear();
      if (running) host.stage("off");
      running = false;
    },
    /** A real mouse is on a chip (skills.tsx): the visit under way ends there, without its tooltip, and none starts
        until release(). The tour still counts as running, so the volleys stay held. */
    hold() {
      held = true;
      clear();
      if (running) host.stage("off");
    },
    /** The mouse has left the chip: the round goes on after one ordinary pause between visits. */
    release() {
      if (!held) return;
      held = false;
      clear();
      if (running) later(visit, between(random, TOUR.pause));
    },
    get running() {
      return running;
    },
  };
}
